import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Channel } from '@conversia/shared-types';
import * as channelsApi from '@/api/channels.api';
import { ApiError } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';

const schema = z.object({
  type: z.enum(['WHATSAPP', 'INSTAGRAM', 'MESSENGER', 'TELEGRAM', 'EMAIL']),
  name: z.string().min(1, 'El nombre es obligatorio').max(120),
});
type FormValues = z.infer<typeof schema>;

export function ChannelConnectModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (channel: Channel) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { type: 'WHATSAPP', name: '' },
  });

  useEffect(() => {
    if (open) reset({ type: 'WHATSAPP', name: '' });
  }, [open, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => channelsApi.createChannel(values),
    onSuccess: (channel) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success('Canal creado', 'Escanea el QR para conectarlo.');
      onCreated(channel);
      onClose();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'No se pudo crear el canal';
      setError('root', { message });
    },
  });

  const onSubmit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Conectar canal"
      description="Por ahora solo WhatsApp está disponible para conexión."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} loading={mutation.isPending}>
            Crear y conectar
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Select
          label="Tipo de canal"
          options={[
            { value: 'WHATSAPP', label: 'WhatsApp' },
            { value: 'INSTAGRAM', label: 'Instagram (próximamente)' },
            { value: 'MESSENGER', label: 'Messenger (próximamente)' },
            { value: 'TELEGRAM', label: 'Telegram (próximamente)' },
            { value: 'EMAIL', label: 'Email (próximamente)' },
          ]}
          {...register('type')}
        />
        <Input
          label="Nombre"
          placeholder="WhatsApp Ventas"
          error={errors.name?.message}
          {...register('name')}
        />
        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      </form>
    </Modal>
  );
}
